import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { ThemeVariables } from "../types";

// Get the initial Store API nonce from the localized theme variables.
// This is used by the WooCommerce Store API to establish a session for guest users,
// which is necessary even for just viewing products.
const wcStoreApiNonce = (window.themeVariables as ThemeVariables | undefined)
  ?.wcStoreApiNonce;

// Create a simplified base query. All the complex logic for managing cart tokens
// from response headers has been removed.
const baseQuery = fetchBaseQuery({
  // The base URL for all API requests, sourced from WordPress.
  baseUrl: (window.themeVariables as ThemeVariables | undefined)?.siteUrl || "",

  // Prepare headers for each request.
  prepareHeaders: (headers) => {
    // If the Store API nonce is available, we add it to the request.
    // This is the only header needed for our simplified, view-only functionality.
    if (wcStoreApiNonce) {
      headers.set("Nonce", wcStoreApiNonce);
    } else {
      console.warn("[API] WC Store API Nonce not found in themeVariables.");
    }
    return headers;
  },
  // Ensure cookies are sent with requests if needed for other purposes (like WP login status).
  credentials: "same-origin",
});

// Create the base API slice using the simplified base query.
export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQuery,

  // Define tag types for caching and invalidation.
  // Cart, Checkout, and Order related tags are removed as they are no longer needed.
  tagTypes: ["Product", "Variation", "Category", "Tag", "Attribute", "Brand"],
  // Endpoints will be injected by other more specific API slices.
  endpoints: () => ({}),
});

// Export the auto-generated hooks, reducer, and middleware for use in the Redux store.
export const {
  util: { getRunningQueriesThunk },
  reducer: apiReducer,
  middleware: apiMiddleware,
} = baseApi;
